/* [PATCH nabava-pregled-fe v1] Pregled v aplikaciji Nabava (NACRT_nabava_pregled_v1_20260928.md, N1–N4).
   Strošek nabave vključuje neodbitni DDV (nezavezanec: ves DDV) — N4. Vpogledi: ponavljajoči stroški, nenavadni računi (N3).
   Gradniki in pomožne funkcije iz PregledPage. Vse številke iz GET /nabava-pregled/{cid}/* (samo branje). */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line, ComposedChart, Bar, BarChart } from 'recharts';
import { X, Loader, Info, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { fmt } from '../lib/izdajaZneski';
import {
  Preset, Primerjava, BARVA, PRESETI, iso, fmtDatum, mesecOznaka, eur, kratko, plusDni, obdobjeZa, minusLeto,
  Delta, Kartica, Okvir, Prazno, Lestvica, koncentracija, tooltipStil,
} from './PregledPage';

type RazclN = 'razred' | 'konto' | 'sm' | 'projekt';
const RAZCL_N: { id: RazclN; naziv: string }[] = [
  { id: 'razred', naziv: 'Vrsta' }, { id: 'konto', naziv: 'Konto' }, { id: 'sm', naziv: 'Stroškovno mesto' }, { id: 'projekt', naziv: 'Projekt' },
];

export function NabavaPregledPage() {
  const { selectedCompany } = useAuthStore();
  const cid = selectedCompany?.id;
  const navigate = useNavigate();

  const [preset, setPreset] = useState<Preset>('letos');
  const [odMeri, setOdMeri] = useState('');
  const [doMeri, setDoMeri] = useState('');
  const [primerjava, setPrimerjava] = useState<Primerjava>('lani');
  const [bruto, setBruto] = useState(false);
  const [samoPotrjeni, setSamoPotrjeni] = useState(false);
  const [razcl, setRazcl] = useState<RazclN>('razred');
  const [podatki, setPodatki] = useState<any>(null);
  const [nalaganje, setNalaganje] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [drill, setDrill] = useState<any>(null);

  const [od, doDat] = useMemo<[string, string]>(
    () => (preset === 'po_meri' && odMeri && doMeri ? [odMeri, doMeri] : obdobjeZa(preset, new Date())),
    [preset, odMeri, doMeri]);

  const nalozi = useCallback(async () => {
    if (!cid) return;
    setNalaganje(true); setNapaka(null);
    const pot = `/nabava-pregled/${cid}`;
    const osn: any = { od, 'do': doDat, samo_potrjeni: samoPotrjeni };
    const b: any = { ...osn, bruto };
    const lani: any = { ...b, od: minusLeto(od), 'do': minusLeto(doDat) };
    try {
      const r = await Promise.all([
        api.get(`${pot}/stanje`),
        api.get(`${pot}/povzetek`, { params: b }),
        api.get(`${pot}/serija`, { params: { ...b, razclenitev: 'mesec' } }),
        api.get(`${pot}/serija`, { params: { ...lani, razclenitev: 'mesec' } }),
        api.get(`${pot}/serija`, { params: { ...b, razclenitev: 'partner', limit: 10 } }),
        api.get(`${pot}/serija`, { params: { ...osn, razclenitev: razcl, limit: 12 } }),
        api.get(`${pot}/dobavitelji-novi`, { params: b }),
        api.get(`${pot}/obveznosti`, { params: { samo_potrjeni: samoPotrjeni } }),
        api.get(`${pot}/vpogledi`),
      ]);
      setPodatki({ stanje: r[0].data, povzetek: r[1].data, mes: r[2].data, mesL: r[3].data, dobavitelji: r[4].data,
                   razcl: r[5].data, novi: r[6].data, obveznosti: r[7].data, vpogledi: r[8].data });
    } catch (e: any) {
      setNapaka(e?.response?.data?.detail || 'Podatkov ni bilo mogoče naložiti.');
    } finally {
      setNalaganje(false);
    }
  }, [cid, od, doDat, bruto, samoPotrjeni, razcl]);

  useEffect(() => { nalozi(); }, [nalozi]);

  useEffect(() => {
    if (!drill || !drill.nalaganje || !cid) return;
    api.get(`/nabava-pregled/${cid}/racuni`, { params: { od, 'do': doDat, samo_potrjeni: samoPotrjeni, ...drill.params } })
      .then((r: any) => setDrill((d: any) => d && { ...d, podatki: r.data, nalaganje: false }))
      .catch((e: any) => setDrill((d: any) => d && { ...d, napaka: e?.response?.data?.detail || 'Seznama ni bilo mogoče naložiti.', nalaganje: false }));
  }, [drill, cid, od, doDat, samoPotrjeni]);
  const odpri = (naslov: string, params: any) => setDrill({ naslov, params, podatki: null, nalaganje: true });

  const mesecni = useMemo(() => {
    if (!podatki) return [];
    const p = podatki.mes.tocke || []; const pl = podatki.mesL?.tocke || [];
    let k = 0; let kl = 0;
    return p.map((t: any, i: number) => {
      const lv = pl[i]?.vrednost || 0; k += t.vrednost; kl += lv;
      return { kljuc: t.kljuc, oznaka: mesecOznaka(t.kljuc), nabava: t.vrednost, nabavaL: lv, kum: k, kumL: kl };
    });
  }, [podatki]);

  if (!cid) return <div className="p-6 text-sm text-gray-500">Najprej izberite podjetje v zgornjem izbirniku.</div>;

  const pv = podatki?.povzetek;
  const ref = pv ? (primerjava === 'lani' ? pv.lani : primerjava === 'prejsnje' ? pv.prejsnje : null) : null;
  const pokaziLani = primerjava === 'lani' && !!pv && pv.lani.st_racunov + pv.lani.st_dobropisov > 0;
  const st = podatki?.stanje;
  const obv = podatki?.obveznosti;
  const nov = podatki?.novi;
  const vp = podatki?.vpogledi;
  const danes = new Date();
  const kosiObv: any[] = obv && obv.na_voljo ? [
    { kljuc: 'ni_zapadlo', naziv: 'Še ni zapadlo', params: { rok_od: iso(danes) } },
    { kljuc: 'd1_30', naziv: 'Zapadlo 1–30 dni', params: { rok_od: iso(plusDni(danes, -30)), rok_do: iso(plusDni(danes, -1)) } },
    { kljuc: 'd31_60', naziv: 'Zapadlo 31–60 dni', params: { rok_od: iso(plusDni(danes, -60)), rok_do: iso(plusDni(danes, -31)) } },
    { kljuc: 'd61_90', naziv: 'Zapadlo 61–90 dni', params: { rok_od: iso(plusDni(danes, -90)), rok_do: iso(plusDni(danes, -61)) } },
    { kljuc: 'nad_90', naziv: 'Zapadlo več kot 90 dni', params: { rok_do: iso(plusDni(danes, -91)) } },
  ].map((k) => ({ ...k, vrednost: obv.kosi[k.kljuc].znesek, st: obv.kosi[k.kljuc].st })) : [];

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Pregled nabave</h1>
          <div className="text-xs text-gray-500">{(selectedCompany as any)?.name} · {fmtDatum(od)} – {fmtDatum(doDat)}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <div className="flex flex-wrap rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {PRESETI.map((p) => (
              <button key={p.id} type="button" onClick={() => setPreset(p.id)}
                className={'px-2.5 py-1.5 ' + (preset === p.id ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800')}>
                {p.naziv}
              </button>
            ))}
          </div>
          {preset === 'po_meri' && (
            <span className="flex items-center gap-1">
              <input type="date" value={odMeri} onChange={(e) => setOdMeri(e.target.value)} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900" />
              –
              <input type="date" value={doMeri} onChange={(e) => setDoMeri(e.target.value)} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900" />
            </span>
          )}
          <select value={primerjava} onChange={(e) => setPrimerjava(e.target.value as Primerjava)}
            className="px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <option value="lani">Primerjava: isto obdobje lani</option>
            <option value="prejsnje">Primerjava: prejšnje obdobje</option>
            <option value="brez">Brez primerjave</option>
          </select>
          <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={bruto} onChange={(e) => setBruto(e.target.checked)} /> z DDV</label>
          <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={samoPotrjeni} onChange={(e) => setSamoPotrjeni(e.target.checked)} /> samo potrjeni</label>
          {nalaganje && <Loader className="w-4 h-4 animate-spin text-blue-500" />}
        </div>
      </div>

      {napaka && <div className="rounded-lg bg-rose-50 dark:bg-rose-950 text-rose-700 dark:text-rose-300 text-sm p-3">{napaka}</div>}

      {st && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500 bg-gray-100 dark:bg-gray-800/60 rounded-lg px-3 py-2">
          <Info className="w-3.5 h-3.5" />
          <span>Zadnji vnos prejetih: {fmtDatum(st.zadnji_vnos_prejeti)}</span>
          <span>Nepotrjenih: {st.nepotrjenih}{samoPotrjeni ? ' (izključeni)' : ' (vključeni)'}</span>
          {pv && !bruto && <span>Nabava vključuje {pv.obdobje.nabava_vkljucuje}.</span>}
        </div>
      )}

      {pv && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kartica naslov={'Nabava' + (bruto ? ' (z DDV)' : '')} vrednost={eur(pv.obdobje.nabava)}
              opomba={`${pv.obdobje.st_racunov} računov · ${pv.obdobje.st_dobropisov} dobropisov`}
              onClick={() => odpri('Prejeti računi', {})}>
              {ref && <Delta zdaj={pv.obdobje.nabava} prej={ref.nabava} st={ref.st_racunov + ref.st_dobropisov} obrnjeno />}
            </Kartica>
            <Kartica naslov="Število računov" vrednost={String(pv.obdobje.st_racunov)}>
              {ref && <Delta zdaj={pv.obdobje.st_racunov} prej={ref.st_racunov} st={ref.st_racunov} obrnjeno />}
            </Kartica>
            <Kartica naslov="Povprečni račun" vrednost={eur(pv.obdobje.povprecni)} opomba="nabava / število računov">
              {ref && <Delta zdaj={pv.obdobje.povprecni} prej={ref.povprecni} st={ref.st_racunov} obrnjeno />}
            </Kartica>
            <Kartica naslov="Dobaviteljev" vrednost={String(pv.obdobje.dobaviteljev)}>
              {ref && <Delta zdaj={pv.obdobje.dobaviteljev} prej={ref.dobaviteljev} st={ref.st_racunov} obrnjeno />}
            </Kartica>
            <Kartica naslov="Vstopni DDV (odbitni)" vrednost={pv.obdobje.ddv_na_voljo ? eur(pv.obdobje.vstopni_ddv) : '—'}
              opomba={pv.obdobje.ddv_na_voljo ? 'os: datum DDV' : 'podjetje ni zavezanec za DDV'} />
            <Kartica naslov="Obveznosti v 7 dneh"
              vrednost={obv && obv.na_voljo ? eur(obv.zapade_7.znesek) : '—'}
              opomba={obv && obv.na_voljo ? `zapadlo neplačano: ${eur(obv.zapadlo.znesek)}` : 'ni na voljo'}
              onClick={obv && obv.na_voljo ? () => odpri('Obveznosti — zapade v 7 dneh', { odprti: true, rok_od: iso(danes), rok_do: iso(plusDni(danes, 7)) }) : undefined} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <Okvir naslov="Nabava po mesecih" opomba={(bruto ? 'z DDV' : 'brez odbitnega DDV') + ' · os: datum storitve · klik na stolpec odpre račune' + (pokaziLani ? ' · črtkano: lani' : '')}>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={mesecni}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="oznaka" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={kratko} tick={{ fontSize: 11 }} width={48} />
                    <Tooltip formatter={(v: any) => eur(v)} contentStyle={tooltipStil} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="nabava" name="Nabava" fill={BARVA.nabava} cursor="pointer"
                      onClick={(d: any) => { const e = d?.payload || d; odpri('Prejeti računi — ' + e.oznaka, { mesec: e.kljuc }); }} />
                    {pokaziLani && <Line dataKey="nabavaL" name="Nabava lani" stroke={BARVA.nabava} strokeDasharray="5 4" dot={false} />}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Okvir>
            <Okvir naslov="Kumulativa v obdobju" opomba={pokaziLani ? 'polno: letos · črtkano: lani' : 'seštevek po mesecih'}>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mesecni}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="oznaka" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={kratko} tick={{ fontSize: 11 }} width={48} />
                    <Tooltip formatter={(v: any) => eur(v)} contentStyle={tooltipStil} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line dataKey="kum" name="Nabava" stroke={BARVA.nabava} strokeWidth={2} dot={false} />
                    {pokaziLani && <Line dataKey="kumL" name="Nabava lani" stroke={BARVA.nabava} strokeDasharray="5 4" dot={false} />}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Okvir>
          </div>

          {vp && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              <Okvir naslov="Ponavljajoči stroški"
                opomba={`računi v vsaj ${vp.ponavljajoci.pravilo.vsaj} od zadnjih ${vp.ponavljajoci.pravilo.mesecev} celih mesecev, podoben znesek · mesečno ${eur(vp.ponavljajoci.mesecno)} · letno ${eur(vp.ponavljajoci.letno)}`}>
                <Lestvica tocke={vp.ponavljajoci.tocke} barva={BARVA.nabava}
                  oznaka={(t: any) => `${t.naziv} · ${t.mesecev} mes. · letno ${eur(t.letno)}`}
                  onKlik={(t: any) => odpri('Prejeti računi — ' + t.naziv + ' (zadnjih 6 mesecev)', { partner: t.kljuc, od: vp.ponavljajoci.od, 'do': vp.ponavljajoci.do })} />
              </Okvir>
              <Okvir naslov="Nenavadni računi"
                opomba={`zadnjih ${vp.nenavadni.pravilo.dni} dni · več kot ${vp.nenavadni.pravilo.krat}× mediana prejšnjih računov istega dobavitelja in vsaj ${eur(vp.nenavadni.pravilo.presezek)} nad njo`}>
                {vp.nenavadni.tocke.length ? (
                  <div className="space-y-1.5">
                    {vp.nenavadni.tocke.slice(0, 10).map((t: any) => (
                      <button key={t.id} type="button" onClick={() => odpri('Prejeti računi — ' + t.naziv, { partner: t.kljuc, od: vp.nenavadni.od, 'do': vp.nenavadni.do })}
                        className="w-full text-left flex justify-between gap-2 text-xs hover:text-blue-600">
                        <span className="truncate text-gray-700 dark:text-gray-300">{fmtDatum(t.datum)} · {t.naziv} · {t.stevilka}</span>
                        <span className="whitespace-nowrap font-mono">{eur(t.znesek)} <span className="text-gray-400">({t.krat}× od {eur(t.mediana)})</span></span>
                      </button>
                    ))}
                  </div>
                ) : <Prazno besedilo="V zadnjih 90 dneh ni nenavadnih računov." />}
              </Okvir>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <Okvir naslov="Največji dobavitelji" opomba={koncentracija(podatki.dobavitelji) || 'nabava po dobavitelju'}>
              <Lestvica tocke={podatki.dobavitelji.tocke} ostalo={podatki.dobavitelji.ostalo} barva={BARVA.nabava}
                onKlik={(t: any) => odpri('Prejeti računi — ' + t.naziv, { partner: t.kljuc })} />
            </Okvir>
            <Okvir naslov="Novi dobavitelji" opomba={`prvi račun v obdobju, brez računa v 12 mesecih pred tem · ${nov?.st || 0} · ${eur(nov?.vrednost)}`}>
              {nov && !nov.zanesljivo && (
                <div className="mb-3 text-[11px] rounded bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 px-2 py-1">
                  Prejeti računi segajo šele od {fmtDatum(nov.najstarejsi_racun)} — novih dobaviteljev ni mogoče zanesljivo oceniti, zato seznam ni prikazan.
                </div>
              )}
              {nov?.zanesljivo ? /* [PATCH pregled-lestvica v1] */ <Lestvica tocke={nov?.tocke} ostalo={nov?.ostalo} barva={BARVA.nabava}
                oznaka={(t: any) => `${t.naziv}${t.prvi ? ' · od ' + fmtDatum(t.prvi) : ''}`}
                onKlik={(t: any) => odpri('Prejeti računi — ' + t.naziv, { partner: t.kljuc })} /> : <Prazno besedilo="Seznam ni prikazan — podatki ne segajo 12 mesecev pred obdobjem." />}
            </Okvir>
          </div>

          <Okvir naslov="Kam gre denar" opomba="nabava po knjižbah (z neodbitnim DDV) · klik odpre račune"
            desno={
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs">
                {RAZCL_N.map((r) => (
                  <button key={r.id} type="button" onClick={() => setRazcl(r.id)}
                    className={'px-2 py-1 ' + (razcl === r.id ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300')}>
                    {r.naziv}
                  </button>
                ))}
              </div>
            }>
            <Lestvica tocke={podatki.razcl.tocke} ostalo={podatki.razcl.ostalo} barva={BARVA.nabava}
              oznaka={(t: any) => (razcl === 'konto' ? `${t.kljuc} ${t.naziv || ''}` : (t.naziv || t.kljuc))}
              onKlik={(t: any) => odpri('Nabava — ' + (razcl === 'konto' ? t.kljuc : t.naziv), { [razcl]: t.kljuc })} />
          </Okvir>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <Okvir naslov="Starost neplačanih obveznosti" opomba="upošteva plačila, označena v aplikaciji">
              {obv && obv.na_voljo
                ? <Lestvica tocke={kosiObv} barva={BARVA.rok} oznaka={(t: any) => `${t.naziv} (${t.st})`}
                    onKlik={(t: any) => odpri('Obveznosti — ' + t.naziv, { odprti: true, ...t.params })} />
                : <Prazno besedilo={(obv?.razlog || 'Ni na voljo.') + (obv ? ` Označenih plačil med zapadlimi v zadnjih 90 dneh: ${Math.round((obv.delez_placanih_90 || 0) * 100)} % (prag ${Math.round((obv.prag || 0.5) * 100)} %).` : '')} />}
            </Okvir>
            <Okvir naslov="Koledar plačil — naslednjih 30 dni" opomba="neplačani prejeti po datumu zapadlosti · klik odpre račune">
              {obv && obv.na_voljo ? (
                (obv.koledar || []).length ? (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={(obv.koledar || []).map((k: any) => ({ ...k, oznaka: fmtDatum(k.datum).slice(0, 6) }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="oznaka" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={kratko} tick={{ fontSize: 11 }} width={48} />
                        <Tooltip formatter={(v: any) => eur(v)} contentStyle={tooltipStil} />
                        <Bar dataKey="znesek" name="Za plačilo" fill={BARVA.rok} cursor="pointer"
                          onClick={(d: any) => { const e = d?.payload || d; odpri('Obveznosti — rok ' + fmtDatum(e.datum), { odprti: true, rok_od: e.datum, rok_do: e.datum }); }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : <Prazno besedilo="V naslednjih 30 dneh ni zapadlih neplačanih obveznosti." />
              ) : <Prazno besedilo="Ni na voljo (glej levo)." />}
            </Okvir>
          </div>
        </>
      )}

      {drill && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setDrill(null)}>
          <div className="h-full w-full max-w-3xl bg-white dark:bg-gray-900 shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{drill.naslov}</div>
                <div className="text-[11px] text-gray-400">{drill.params?.odprti ? 'odprte postavke po zapadlosti' : `${fmtDatum(drill.params?.od || od)} – ${fmtDatum(drill.params?.do || doDat)}`}</div>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => navigate('/dokumenti?tip=prejeti_racun')}
                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <ExternalLink className="w-3.5 h-3.5" /> Odpri seznam prejetih
                </button>
                <button type="button" onClick={() => setDrill(null)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {drill.nalaganje && <div className="p-6 flex justify-center"><Loader className="w-6 h-6 animate-spin text-blue-500" /></div>}
              {drill.napaka && <div className="p-4 text-sm text-rose-600">{drill.napaka}</div>}
              {drill.podatki && (
                drill.podatki.racuni.length ? (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2">{drill.params?.odprti ? 'Rok' : 'Datum'}</th>
                        <th className="text-left px-2 py-2">Številka</th>
                        <th className="text-left px-2 py-2">Interna</th>
                        <th className="text-left px-2 py-2">Dobavitelj</th>
                        <th className="text-right px-2 py-2">Osnova</th>
                        <th className="text-right px-2 py-2">Strošek</th>
                        <th className="text-right px-2 py-2">Skupaj</th>
                        <th className="text-right px-2 py-2">Odprto</th>
                        <th className="text-left px-3 py-2">Stanje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drill.podatki.racuni.map((r: any) => (
                        <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtDatum(drill.params?.odprti ? r.rok : r.datum)}</td>
                          <td className="px-2 py-1.5">{r.stevilka || '—'}{r.doc_type === 'credit_note' && <span className="ml-1 text-[10px] text-rose-600">dobropis</span>}</td>
                          <td className="px-2 py-1.5 text-gray-500">{r.interna || ''}</td>
                          <td className="px-2 py-1.5 truncate max-w-[12rem]">{r.partner || '—'}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmt(r.osnova)}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmt(r.strosek)}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmt(r.skupaj)}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{fmt(r.odprto)}</td>
                          <td className="px-3 py-1.5 text-gray-500">{r.status === 'approved' ? 'potrjen' : 'nepotrjen'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <Prazno besedilo="Ni računov za ta izbor." />
              )}
            </div>
            {drill.podatki && (
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-300 flex flex-wrap gap-x-4">
                <span>Računov: {drill.podatki.st}{drill.podatki.omejeno ? ` (prikazanih ${drill.podatki.racuni.length})` : ''}</span>
                <span>Osnova: {eur(drill.podatki.osnova)}</span>
                {drill.podatki.strosek != null && <span>Strošek: {eur(drill.podatki.strosek)}</span>}
                <span>Skupaj: {eur(drill.podatki.skupaj)}</span>
                {drill.podatki.odprto != null && <span>Odprto: {eur(drill.podatki.odprto)}</span>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
